import * as yup from 'yup';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetScannerQuery as GetScanner } from '__generated__/graphql';

import { encodeUrl, getServerErrors } from '../../../common/helpers';
import RegistrationFilters, {
  RegistrationFilterParams, useRegistrationFilterMapping,
} from '../Registrations/RegistrationFilters';
import UI from '../../../common/UI';
import useEditScannerMutation from './useEditScannerMutation';
import useNavigationPrompt from '../../../common/useNavigationPrompt';
import useProject from '../../useProject';
import useValidation from '../../../common/useValidation';

type Scanner = GetScanner['project']['scanner'];

const editScannerCriteriaSchema = yup.object().shape({});

export interface EditScannerCriteriaFormProps {
  scanner: Scanner;
}

const EditScannerCriteriaForm = ({ scanner }: EditScannerCriteriaFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const { mapServerOutputToRegistrationFilters, mapRegistrationFiltersToServerInput } = useRegistrationFilterMapping();

  const getValues = (scanner: Scanner) => ({
    filters: mapServerOutputToRegistrationFilters(scanner.filters),
  });

  const form = useForm({
    values: getValues(scanner),
  });

  useNavigationPrompt(form.changed());

  const [editScanner, { error, loading }] = useEditScannerMutation({
    input: {
      id: scanner.id,
      filters: mapRegistrationFiltersToServerInput(form.values.filters),
    },
    onSuccess: (scanner) => {
      form.reset(getValues(scanner));
    },
  });

  const handleFiltersChange = (newFilters: RegistrationFilterParams) => {
    form.set((values) => ({
      ...values,
      filters: newFilters,
    }));
  };

  const { valid } = useValidation({
    schema: editScannerCriteriaSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editScanner()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />
        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('criteria')}</UI.Legend>
          <UI.InputDescription>
            {t('scanner_criteria_description')}
          </UI.InputDescription>
        </UI.Div>
        <RegistrationFilters
          filters={form.values.filters}
          onChange={handleFiltersChange}
          types={[
            'tickets', 'ticket_number_sequences', 'products', 'participant_fields', 'invitations', 'coupons', 'team',
          ]}
          showResetButton={false}
          showViewRegistrationsButton
        />
        <UI.HR />
        <UI.FormButtons
          form={form}
          submitLabel={t('save_scanner')}
          closeTo={`/${project.id}/participants/scanners?${encodeUrl({ event: scanner.event.id })}`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditScannerCriteriaForm;
